import { atom, useAtom, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';

import { useFillHyphenIntoPhoneNumber } from '@/lib/utils/businessLogics/utils/_helper';
import { sendLog } from '@/lib/utils/send-log';
import { webinarOrientationAtom } from '@/store/webinar';

import { multiFormStepAtom } from '../MultiStepForm/MultiStepForm';
import Selector from './components/Selector';
import * as S from './WebinarApplicationModal.style';

export const skipNumAtom = atom(-1);
export const STEP_DURATION = 100;

const sendPopupLog = (pageName: string, pageNum: number) => {
  sendLog('hh_webinarDetailPage_Popup_view', {
    page_name: pageName,
    page_number: pageNum,
  });
};
export function PersonalInfo() {
  const fillHyphenInfoPhoneNumber = useFillHyphenIntoPhoneNumber();
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);
  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = value.replace(/[^0-9]/g, '');
    }
    setOrientation({
      ...orientation,
      [name]: value,
    });
  };

  useEffect(() => {
    sendPopupLog('Personal Info', 0);
  }, []);

  return (
    <S.QuestionWrapper>
      <S.InputsWrapper>
        <S.Label>
          <p>이름</p>
          <S.Input
            name='name'
            placeholder='이름'
            value={orientation.name}
            onChange={onChangeHandler}
          />
        </S.Label>
        <S.Label>
          <p>전화번호</p>
          <S.Input
            name='phone'
            placeholder='010-0000-0000'
            value={fillHyphenInfoPhoneNumber(orientation.phone)}
            onChange={onChangeHandler}
          />
        </S.Label>
        <S.Label>
          <p>
            이메일 <span>(정보 수신용)</span>
          </p>
          <S.Input
            name='email'
            placeholder='example@gmail.com'
            value={orientation.email}
            onChange={onChangeHandler}
          />
        </S.Label>
      </S.InputsWrapper>
    </S.QuestionWrapper>
  );
}

export const CurrentStateSurvey = () => {
  const values = [
    '첫 취업 준비 중 (개발 전공자)',
    '첫 취업 준비 중 (개발 비전공자)',
    '이직 준비 중 (개발자 경험 O)',
    '이직 준비 중 (타 직무 경험 O)',
    '기타',
  ];
  const surveyIdx = 0;
  const setStep = useSetAtom(multiFormStepAtom);
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);

  const onClickItem = (value: string) => {
    const survey = [...orientation.survey];
    survey[surveyIdx].answer = value;
    setOrientation((prev) => ({
      ...prev,
      survey: survey,
    }));
    const timeout = setTimeout(() => {
      setStep((prev) => prev + 1);
      clearTimeout(timeout);
    }, STEP_DURATION);
  };
  useEffect(() => {
    sendPopupLog('Experience', 1);
  }, []);

  return (
    <S.QuestionWrapper>
      <S.StepQuestion>{orientation.survey[surveyIdx].question}</S.StepQuestion>
      <Selector
        values={values}
        selectedValue={orientation.survey[surveyIdx].answer}
        onClickItem={onClickItem}
      />
    </S.QuestionWrapper>
  );
};

export const DifficultySurvey = () => {
  const values = [
    '이력서를 작성하는 것이 어렵다.',
    '면접 준비를 어떻게 해야 할지 모르겠다.',
    '기초/알고리즘이 부족한 것 같다.',
    '프로젝트를 더 해야 할 것 같다.',
    '내가 무엇이 부족한지 잘 모르겠다.',
    '기타',
  ];
  const surveyIdx = 1;
  const setStep = useSetAtom(multiFormStepAtom);
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);

  const onClickItem = (value: string) => {
    const survey = [...orientation.survey];
    survey[surveyIdx].answer = value;
    setOrientation((prev) => ({
      ...prev,
      survey: survey,
    }));
    const timeout = setTimeout(() => {
      setStep((prev) => prev + 1);
      clearTimeout(timeout);
    }, STEP_DURATION);
  };
  useEffect(() => {
    sendPopupLog('Intention', 2);
  }, []);

  return (
    <S.QuestionWrapper>
      <S.StepQuestion>{orientation.survey[surveyIdx].question}</S.StepQuestion>
      <Selector
        values={values}
        selectedValue={orientation.survey[surveyIdx].answer}
        onClickItem={onClickItem}
      />
    </S.QuestionWrapper>
  );
};

export const RecruitSurvey = () => {
  const values = ['6개월 이내', '1~2년 내외', '아직 계획 없음'];
  const surveyIdx = 2;
  const setStep = useSetAtom(multiFormStepAtom);
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);
  const onClickItem = (value: string) => {
    const survey = [...orientation.survey];
    survey[surveyIdx].answer = value;
    setOrientation((prev) => ({
      ...prev,
      survey: survey,
    }));
    const timeout = setTimeout(() => {
      setStep((prev) => prev + 1);
      clearTimeout(timeout);
    }, STEP_DURATION);
  };

  useEffect(() => {
    sendPopupLog('Time', 3);
  }, []);

  return (
    <S.QuestionWrapper>
      <S.StepQuestion>{orientation.survey[surveyIdx].question}</S.StepQuestion>
      <Selector
        values={values}
        selectedValue={orientation.survey[surveyIdx].answer}
        onClickItem={onClickItem}
      />
    </S.QuestionWrapper>
  );
};

export function OrientationQuestion() {
  const [orientation, setOrientation] = useAtom(webinarOrientationAtom);
  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setOrientation({
      ...orientation,
      [name]: value,
    });
  };

  useEffect(() => {
    sendPopupLog('Pre-Question', 4);
  }, []);

  return (
    <S.QuestionWrapper>
      <S.StepQuestion>
        연사님께 드리고 싶은 질문이 있다면 알려주세요. <span>(선택)</span>
      </S.StepQuestion>
      <S.Textarea
        name='question'
        placeholder='질문을 남겨주세요.'
        value={orientation.question}
        onChange={onChangeHandler}
      />
    </S.QuestionWrapper>
  );
}
